import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { productsAPI, sizeOptionsAPI, pricesAPI, categoriesAPI, productImagesAPI } from '../../services/api';
import Button from './Button';
import Input from './Input';
import ImageUpload from './ImageUpload';
import { X, ChevronRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import './ProductWizardDialog.css';

const ProductWizardDialog = ({ isOpen, onClose, mode = 'create', productId = null, sizeId = null }) => {
  const queryClient = useQueryClient();

  // Determine initial stage based on mode
  // 'create' - new product, start at stage 1
  // 'editProduct' - edit product details, start at stage 1
  // 'addSize' - add new size to existing product, start at stage 2
  // 'editSize' - edit existing size, start at stage 2
  const getInitialStage = () => {
    if (mode === 'addSize' || mode === 'editSize') return 2;
    return 1;
  };

  // Stage: 1 = Product Details, 2 = Size Options
  const [stage, setStage] = useState(getInitialStage());

  // Product data
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    categoryId: '',
    isActive: true,
  });

  // Current size being edited/added
  const [currentSize, setCurrentSize] = useState({
    sizeOptionId: '',
    price: '',
    description: '',
    image: null,
  });

  // Track the product ID after creation
  const [createdProductId, setCreatedProductId] = useState(productId);

  // Track uploaded image
  const [uploadingImage, setUploadingImage] = useState(false);

  // Track if we're adding another size (to reset image display)
  const [isAddingAnother, setIsAddingAnother] = useState(false);

  // Fetch categories
  const { data: categoriesData } = useQuery('categories', async () => {
    const response = await categoriesAPI.list();
    return response.data.data;
  }, { enabled: isOpen });

  // Fetch size options
  const { data: sizeOptionsData } = useQuery('size-options', async () => {
    const response = await sizeOptionsAPI.list();
    return response.data.data;
  }, { enabled: isOpen });

  // Fetch product details if editing
  const { data: productDetails } = useQuery(
    ['product', createdProductId],
    async () => {
      const response = await productsAPI.getById(createdProductId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(createdProductId) }
  );

  // Fetch size price details if editing a specific size
  const { data: sizePrice } = useQuery(
    ['price', sizeId],
    async () => {
      const response = await pricesAPI.getById(sizeId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(sizeId) }
  );

  // Fetch product image for the size
  const { data: productImages, refetch: refetchImages } = useQuery(
    ['productImages', createdProductId],
    async () => {
      const response = await productImagesAPI.listByProduct(createdProductId);
      return response.data.data;
    },
    { enabled: isOpen && Boolean(createdProductId) }
  );

  // Mutations
  const createProductMutation = useMutation(
    async (data) => {
      const response = await productsAPI.create(data);
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('products');
        setCreatedProductId(data.id);
      },
    }
  );

  const updateProductMutation = useMutation(
    async ({ id, data }) => {
      const response = await productsAPI.update(id, data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries(['product', createdProductId]);
      },
    }
  );

  const createPriceMutation = useMutation(
    async (data) => {
      const response = await pricesAPI.create(data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
        queryClient.invalidateQueries('products');
      },
    }
  );

  const updatePriceMutation = useMutation(
    async ({ id, data }) => {
      const response = await pricesAPI.update(id, data);
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
        queryClient.invalidateQueries('products');
      },
    }
  );

  const uploadProductImageMutation = useMutation(
    ({ sizePriceId, file }) => productImagesAPI.upload(sizePriceId, file),
    {
      onMutate: () => {
        setUploadingImage(true);
      },
      onSuccess: () => {
        toast.success('Image uploaded successfully!');
        refetchImages();
      },
      onError: (error) => {
        toast.error(error.data?.error || error.message || 'Failed to upload image');
      },
      onSettled: () => {
        setUploadingImage(false);
      },
    }
  );

  const deleteProductImageMutation = useMutation(
    (sizePriceId) => productImagesAPI.delete(sizePriceId),
    {
      onSuccess: () => {
        toast.success('Image deleted successfully!');
        refetchImages();
      },
      onError: (error) => {
        toast.error(error.data?.error || 'Failed to delete image');
      },
    }
  );

  // Load product data if editing
  useEffect(() => {
    if (productDetails) {
      setProductData({
        name: productDetails.name,
        description: productDetails.description || '',
        categoryId: productDetails.category_id || '',
        isActive: productDetails.is_active,
      });
    }
  }, [productDetails]);

  // Load size data if editing a specific size
  useEffect(() => {
    if (sizePrice && sizeOptionsData && mode === 'editSize') {
      setCurrentSize({
        sizeOptionId: sizePrice.size_option_id,
        price: sizePrice.price,
        description: sizePrice.description || '',
        image: null,
      });
      setIsAddingAnother(false);
    } else if (sizeOptionsData && mode !== 'editSize') {
      // Default to "Standard" for new sizes
      const standardOption = sizeOptionsData.find(opt => opt.name === 'Standard');
      if (standardOption) {
        setCurrentSize(prev => ({
          ...prev,
          sizeOptionId: standardOption.id,
        }));
      }
    }
  }, [sizePrice, sizeOptionsData, mode]);

  const handleClose = () => {
    setStage(1);
    setProductData({
      name: '',
      description: '',
      categoryId: '',
      isActive: true,
    });
    setCurrentSize({
      sizeOptionId: '',
      price: '',
      description: '',
      image: null,
    });
    setCreatedProductId(null);
    setIsAddingAnother(false);
    onClose();
  };

  const handleProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSizeInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSize((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNextStage = async () => {
    if (stage === 1) {
      // Validate product details
      if (!productData.name.trim()) {
        toast.error('Please enter a product name');
        return;
      }

      try {
        if (createdProductId) {
          // Update existing product
          await updateProductMutation.mutateAsync({
            id: createdProductId,
            data: {
              name: productData.name,
              description: productData.description || '',
              categoryId: productData.categoryId ? parseInt(productData.categoryId, 10) : null,
              isActive: productData.isActive,
            },
          });
        } else {
          // Create new product
          const newProduct = await createProductMutation.mutateAsync({
            name: productData.name,
            description: productData.description || '',
            categoryId: productData.categoryId ? parseInt(productData.categoryId, 10) : null,
            isActive: productData.isActive,
          });
          setCreatedProductId(newProduct.id);
        }
        setStage(2);
      } catch (error) {
        toast.error(error.message || 'Failed to save product details');
      }
    }
  };

  const handleSave = async (addAnother = false) => {
    // Validate size details
    if (!currentSize.sizeOptionId) {
      toast.error('Please select a size option');
      return;
    }
    if (!currentSize.price || parseFloat(currentSize.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      const priceData = {
        productId: createdProductId,
        sizeOptionId: parseInt(currentSize.sizeOptionId, 10),
        price: parseFloat(currentSize.price),
        stockQuantity: 0,
        isAvailable: true,
        description: currentSize.description || '',
      };

      let savedPrice;
      if (mode === 'editSize' && sizeId) {
        // Update existing price
        savedPrice = await updatePriceMutation.mutateAsync({
          id: sizeId,
          data: priceData,
        });
        toast.success('Size updated successfully');
      } else {
        // Create new price
        savedPrice = await createPriceMutation.mutateAsync(priceData);
        toast.success('Size added successfully');
      }

      // Upload image if provided
      if (currentSize.image) {
        await uploadProductImageMutation.mutateAsync({
          sizePriceId: savedPrice.id || sizeId,
          file: currentSize.image,
        });
      }

      if (addAnother) {
        // Reset form for another size
        const standardOption = sizeOptionsData.find(opt => opt.name === 'Standard');
        setCurrentSize({
          sizeOptionId: standardOption?.id || '',
          price: '',
          description: '',
          image: null,
        });
        setIsAddingAnother(true);
        refetchImages(); // Refresh images list
      } else {
        // Close dialog
        handleClose();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save size');
    }
  };

  const getImageUrlForSize = () => {
    // Don't show image if adding another size
    if (isAddingAnother) return null;
    if (!productImages || !sizeId) return null;
    const image = productImages.find(img => img.product_size_price_id === parseInt(sizeId));
    return image?.url || null;
  };

  const handleImageUpload = (file) => {
    setCurrentSize(prev => ({ ...prev, image: file }));
  };

  const handleImageDelete = () => {
    if (mode === 'editSize' && sizeId) {
      deleteProductImageMutation.mutate(sizeId);
    }
    setCurrentSize(prev => ({ ...prev, image: null }));
  };

  if (!isOpen) return null;

  return (
    <div className="wizard-dialog-overlay" onClick={handleClose}>
      <div className="wizard-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="wizard-dialog-header">
          <div>
            <h2>
              {mode === 'editSize' ? 'Edit Product Size' :
               mode === 'editProduct' ? 'Edit Product' :
               mode === 'addSize' ? 'Add Size' :
               stage === 1 ? 'Add Product' : 'Add Size Options'}
            </h2>
            <div className="wizard-stages">
              <span className={`stage ${stage === 1 ? 'active' : 'completed'}`}>1. Product Details</span>
              <ChevronRight size={16} className="stage-separator" />
              <span className={`stage ${stage === 2 ? 'active' : ''}`}>2. Size Options</span>
            </div>
          </div>
          <button className="close-button" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <div className="wizard-dialog-content">
          {stage === 1 && (
            <div className="wizard-stage">
              <h3>Product Information</h3>
              <div className="form-group">
                <Input
                  label="Product Name"
                  name="name"
                  value={productData.name}
                  onChange={handleProductInputChange}
                  required
                  placeholder="e.g., Organic Veg Box"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Category (optional)</label>
                <select
                  name="categoryId"
                  value={productData.categoryId || ''}
                  onChange={handleProductInputChange}
                  className="form-select"
                >
                  <option value="">No category</option>
                  {categoriesData?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={productData.description}
                  onChange={handleProductInputChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Describe your product..."
                />
              </div>

              <div className="form-group">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={productData.isActive}
                    onChange={handleProductInputChange}
                    className="form-checkbox"
                  />
                  <span>Product is active</span>
                </label>
              </div>
            </div>
          )}

          {stage === 2 && (
            <div className="wizard-stage">
              <h3>Size Details</h3>

              <div className="form-group">
                <label className="form-label">Size Option</label>
                <select
                  name="sizeOptionId"
                  value={currentSize.sizeOptionId || ''}
                  onChange={handleSizeInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Select a size</option>
                  {sizeOptionsData?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <Input
                  label="Price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={currentSize.price}
                  onChange={handleSizeInputChange}
                  required
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea
                  name="description"
                  value={currentSize.description}
                  onChange={handleSizeInputChange}
                  className="form-textarea"
                  rows="3"
                  placeholder="Size-specific details..."
                />
              </div>

              <div className="form-group">
                <ImageUpload
                  label="Product Image"
                  currentImageUrl={mode === 'editSize' ? getImageUrlForSize() : null}
                  onUpload={handleImageUpload}
                  onDelete={handleImageDelete}
                  isUploading={uploadingImage}
                  maxSize="512KB"
                  dimensions="Recommended: 1000x1000px"
                  acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                  maxSizeBytes={524288}
                />
              </div>
            </div>
          )}
        </div>

        <div className="wizard-dialog-footer">
          {stage === 1 && (
            <div className="footer-actions">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleNextStage}
                loading={createProductMutation.isLoading || updateProductMutation.isLoading}
              >
                Next
                <ChevronRight size={16} style={{ marginLeft: '4px' }} />
              </Button>
            </div>
          )}

          {stage === 2 && (
            <div className="footer-actions">
              <Button
                type="button"
                onClick={() => handleSave(false)}
                loading={createPriceMutation.isLoading || updatePriceMutation.isLoading}
              >
                Save
              </Button>
              {mode !== 'editSize' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSave(true)}
                  loading={createPriceMutation.isLoading || updatePriceMutation.isLoading}
                >
                  <Plus size={16} style={{ marginRight: '4px' }} />
                  Add Another Size
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductWizardDialog;
